import { defineComponent as _defineComponent } from 'vue'
import { getSearchAnchor } from '@/apis/common/search'
import { getProductionLineSetting } from '@/apis/paibanHistory'
import { useWindowEvent } from '@/hooks/use-event'
import { useRouter } from '@/hooks/useRouter'
import store from '@/store'
import { buryPointFunc } from '@/util/buryPointDeal'
import { message } from 'ant-design-vue'
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'

import { categoryList } from './data'
import { ISearchAnchor } from './types'
import { SearchRegistry } from './utils'

/** 最大历史记录数 */

export default /*#__PURE__*/_defineComponent({
  __name: 'index',
  emits: ['onChange'],
  setup(__props, { expose, emit: emits }) {

const MAX_HISTORY_COUNT = 5
/** localStorage 存储的 key */
const SEARCH_HISTORY_STORAGE_KEY = 'global-search-history'

/** 后台配置的搜索锚点数据 */
const searchAnchors = ref<ISearchAnchor[]>([])
/** 搜索弹窗是否显示 */
const showModal = ref(false)
/** 搜索关键词 */
const searchQuery = ref('')
/** 搜索结果（包含 item 和 matches） */
const searchResults = ref<any[]>([])
/** 搜索历史记录 */
const searchHistory = ref<ISearchAnchor[]>([])
/** 搜索定时器 */
const searchTimer = ref<ReturnType<typeof setTimeout> | null>(null)
/** 当前选中的索引 */
const activeIndex = ref(0)
/** 是否正在使用键盘导航 */
const isKeyboardNavigating = ref(false)
/** 当前选中的生产线 */
const selectedLineId = ref<number | undefined>(undefined)
/** 搜索输入框引用 */
const searchInputRef = ref<HTMLInputElement | null>(null)
/** 分组引用 */
const groupRefs = ref<Record<string, any>>({})
/** 搜索注册实例 */
const searchRegistry = ref<SearchRegistry | null>(null)
/** 是否加载-只有用户打开了弹窗才开始加载数据 */
const isLoadDone = ref(false)
const isLoading = ref(false)

const router = useRouter()

/** 生产线数据 */
const productionLines = ref<{ label: string; value: number }[]>([])



/** 将搜索结果按分类分组 */
const groupedResults = computed(() => {
  if (!searchResults.value.length || !searchQuery.value.trim()) return []
  // 这里可以根据实际需求对搜索结果进行分组
  // 暂时使用简单的分组逻辑：按照路径的第一级分组
  const groups: Record<string, any[]> = {}
  searchResults.value.forEach((result) => {
    // 从 Fuse 结果中提取 item
    const item = result.item
    const matches = result.matches || []
    // 提取分类（可以根据 path 或者自定义字段）
    const category = extractCategory(item)
    if (!groups[category]) {
      groups[category] = []
    }
    // 保存完整的结果（包含 item 和 matches，_matches 用于模板中的高亮显示）
    groups[category].push({ ...item, _matches: matches })
  })
  return Object.entries(groups).map(([className, items]) => ({
    class: className,
    actions: items,
  }))
})

const flatResultItems = computed(() => {
  // 如果有搜索结果，返回搜索结果
  if (searchQuery.value && groupedResults.value.length > 0) {
    return groupedResults.value.map((group) => group.actions).flat()
  }
  // 如果没有搜索词，返回历史记录
  if (!searchQuery.value && searchHistory.value.length > 0) {
    return searchHistory.value
  }
  return []
})

watch(showModal, (newVal) => {
  // 通知外部内部弹窗状态
  emits('onChange', newVal)
  if (newVal) {
    // 阻止背景滚动
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', handleKeydown)
    document.addEventListener('mousemove', handleMouseMove)
    // 稍微延迟避免双击搜索时，输入字符
    setTimeout(() => {
      if (searchInputRef.value) {
        searchInputRef.value.focus()
      }
    }, 50)
    if (!isLoadDone.value) {
      init()
    }
  } else {
    document.body.style.overflow = ''
    document.removeEventListener('keydown', handleKeydown)
    document.removeEventListener('mousemove', handleMouseMove)
  }
})

watch(searchResults, () => {
  activeIndex.value = 0
})

watch(
  () => store.state?.ncSetting?.process_setting_id,
  (newVal: number) => {
    if (newVal) {
      selectedLineId.value = newVal
      handleChangeProductionLine(newVal)
    }
  }
)

onBeforeUnmount(() => {
  if (searchTimer.value) {
    clearTimeout(searchTimer.value)
  }
  document.removeEventListener('keydown', handleKeydown)
  document.removeEventListener('mousemove', handleMouseMove)
  document.body.style.overflow = ''
})

let lastFKeyPressTime = 0
/** 双击间隔时间 */
const DOUBLE_CLICK_DELAY = 300
/**
 * 检查当前是否有焦点元素
 * 如果焦点在可输入元素上（input、textarea、contenteditable等），返回 true
 */
function hasFocusedEditableElement(): boolean {
  const activeElement = document.activeElement
  if (!activeElement) return false

  const tagName = activeElement.tagName.toLowerCase()
  const isContentEditable =
    activeElement.getAttribute('contenteditable') === 'true'

  // 检查是否是可输入元素
  return (
    tagName === 'input' ||
    tagName === 'textarea' ||
    tagName === 'select' ||
    isContentEditable
  )
}

useWindowEvent('keydown', (event: KeyboardEvent) => {
  // 只处理 f 键（不区分大小写）
  if (event.key?.toLowerCase() !== 'f') return
  // 如果已经打开了弹窗，不处理
  if (showModal.value) return
  // 如果当前有焦点元素，不处理
  if (hasFocusedEditableElement()) return
  const currentTime = Date.now()
  const timeSinceLastPress = currentTime - lastFKeyPressTime
  // 判断是否是双击
  if (timeSinceLastPress < DOUBLE_CLICK_DELAY) {
    // 双击 f 键，打开弹窗
    handleExpand()
    // 重置时间，避免连续触发
    lastFKeyPressTime = 0
  } else {
    // 记录本次按下时间
    lastFKeyPressTime = currentTime
  }
})

/**
 * @description 初始化
 */
async function init() {
  try {
    if (isLoading.value) return
    isLoading.value = true
    await getSearchAnchorList()
    await getProductionLineSettingList()
    searchRegistry.value = SearchRegistry.getInstance()
    searchRegistry.value.setAnchors(searchAnchors.value)
    isLoadDone.value = true
    handleSearch()
    loadHistoryFromStorage()
    // 同步历史记录数据：用接口最新数据替换历史记录中对应ID的数据
    syncHistoryWithLatestData()
  } finally {
    isLoading.value = false
  }
}

/**@description 获取搜索锚点列表 */
async function getSearchAnchorList() {
  const res = await getSearchAnchor()
  if (res.status !== 1) {
    message.error(res.msg)
    return
  }
  // 部分参数已经是后面加上去的了，已经有一定数量的数据，部分数据就需要手动初始化一下
  res.data.forEach((v: { documentation: ISearchAnchor }) => {
    const { anchorElement } = v.documentation
    if (
      anchorElement?.highlight === undefined ||
      anchorElement?.highlight === null
    ) {
      anchorElement.highlight = true
    }
  })
  searchAnchors.value = res.data.map((v: any) => ({
    ...v.documentation,
    id: v.id,
  }))
}

/**@description 获取生产线设置 */
async function getProductionLineSettingList() {
  const res = await getProductionLineSetting()
  if (res.status !== 1) {
    message.error(res.msg)
    return
  }
  productionLines.value = res.data.map((item: any) => {
    return { label: item.name, value: item.id }
  })
  if (!selectedLineId.value) {
    selectedLineId.value = res.data[0].id
  }
}

/**
 * @description 提取搜索结果的分类
 * @param item 搜索锚点
 */
function extractCategory(item: ISearchAnchor): string {
  if (item.category) return item.category
  // 优先使用路径的第一级作为分类
  if (item.navigationToApp?.path) {
    const pathParts = item.navigationToApp.path.split('/').filter(Boolean)
    if (pathParts.length > 0) {
      return pathParts[0]
    }
  }
  // 如果没有路径，使用默认分类
  return '其他'
}

/**
 * @description 从 localStorage 加载历史记录
 */
function loadHistoryFromStorage() {
  try {
    const storedHistory = localStorage.getItem(SEARCH_HISTORY_STORAGE_KEY)
    if (storedHistory) {
      const parsed = JSON.parse(storedHistory)
      // 验证数据格式
      if (Array.isArray(parsed)) {
        // 过滤掉不存在的锚点
        const existHistory = parsed.filter((v) =>
          searchAnchors.value.some((v2) => v2.id === v.id)
        )
        searchHistory.value = existHistory.slice(0, MAX_HISTORY_COUNT)
      }
    }
  } catch (error) {
    console.error('加载搜索历史失败:', error)
    // 清除损坏的数据
    localStorage.removeItem(SEARCH_HISTORY_STORAGE_KEY)
  }
}

/**
 * @description 保存历史记录到 localStorage
 */
function saveHistoryToStorage() {
  try {
    localStorage.setItem(
      SEARCH_HISTORY_STORAGE_KEY,
      JSON.stringify(searchHistory.value)
    )
  } catch (error) {
    console.error('保存搜索历史失败:', error)
  }
}

/**
 * @description 同步历史记录数据：用接口最新数据替换历史记录中对应ID的数据
 */
function syncHistoryWithLatestData() {
  if (searchHistory.value.length === 0) return
  // 遍历历史记录，用接口最新数据直接替换
  searchHistory.value = searchHistory.value.map((historyItem) => {
    // 在接口数据中查找对应ID的最新数据
    const latestData = searchAnchors.value.find(
      (anchor) => anchor.id === historyItem.id
    )
    // 直接使用最新数据替换（如果找到的话，否则保持原样）
    return latestData || historyItem
  })
  // 保存到 localStorage
  saveHistoryToStorage()
}

/**
 * @description 添加项目到搜索历史（同步到 localStorage）
 * @param item 搜索锚点
 */
function addToHistory(item: ISearchAnchor) {
  // 移除已存在的相同项（去重）
  const existingIndex = searchHistory.value.findIndex((h) => h.id === item.id)
  if (existingIndex > -1) {
    searchHistory.value.splice(existingIndex, 1)
  }
  // 添加到历史记录开头
  searchHistory.value.unshift(item)
  // 只保留最近 MAX_HISTORY_COUNT 条
  if (searchHistory.value.length > MAX_HISTORY_COUNT) {
    searchHistory.value = searchHistory.value.slice(0, MAX_HISTORY_COUNT)
  }
  // 同步到 localStorage
  saveHistoryToStorage()
}

/**
 * @description 清除搜索历史
 */
function handleClearHistory() {
  searchHistory.value = []
  localStorage.removeItem(SEARCH_HISTORY_STORAGE_KEY)
}

/**
 * @description 根据 matches 信息高亮显示文本
 * @param text 原始文本
 * @param matches 匹配信息数组
 * @param fieldKey 字段名（title 或 description）
 */
function highlightText(text: string, matches: any[], fieldKey: string): string {
  if (!text) return ''
  // 找到当前字段的匹配信息（排除 keywords）
  const fieldMatches = matches.filter(
    (m) => m.key === fieldKey && m.key !== 'keywords'
  )
  if (!fieldMatches.length) return text

  // 收集所有需要高亮的位置
  const highlights: Array<[number, number]> = []
  fieldMatches.forEach((match) => {
    if (match.indices) {
      match.indices.forEach((index: number[]) => {
        highlights.push([index[0], index[1]])
      })
    }
  })

  // 如果没有高亮位置，直接返回原文本
  if (!highlights.length) return text

  // 获取搜索关键词长度
  const searchKeyword = searchQuery.value.trim()
  const keywordLength = searchKeyword.length

  // 合并重叠的高亮区间
  highlights.sort((a, b) => a[0] - b[0])
  const merged: Array<[number, number]> = [highlights[0]]
  for (let i = 1; i < highlights.length; i++) {
    const last = merged[merged.length - 1]
    const current = highlights[i]
    if (current[0] <= last[1] + 1) {
      last[1] = Math.max(last[1], current[1])
    } else {
      merged.push(current)
    }
  }

  // 检查是否存在完整匹配（连续且长度等于搜索词）
  const hasCompleteMatch = merged.some(([start, end]) => {
    const matchLength = end - start + 1
    const matchedText = text.slice(start, end + 1)
    // 匹配长度等于搜索词长度，且内容相同（忽略大小写）
    return (
      matchLength === keywordLength &&
      matchedText.toLowerCase() === searchKeyword.toLowerCase()
    )
  })

  // 如果存在完整匹配，只保留完整匹配的区间
  let finalMerged = merged
  if (hasCompleteMatch) {
    finalMerged = merged.filter(([start, end]) => {
      const matchLength = end - start + 1
      const matchedText = text.slice(start, end + 1)
      return (
        matchLength === keywordLength &&
        matchedText.toLowerCase() === searchKeyword.toLowerCase()
      )
    })
  } else {
    // 如果没有完整匹配，只保留最长的匹配片段
    // 计算每个区间的长度
    const lengthMap = merged.map(([start, end]) => end - start + 1)
    // 找到最大匹配长度
    const maxLength = Math.max(...lengthMap)
    // 只保留最大长度的区间
    finalMerged = merged.filter(([start, end]) => {
      return end - start + 1 === maxLength
    })
  }

  // 从后往前插入高亮标签，避免索引变化
  let result = text
  for (let i = finalMerged.length - 1; i >= 0; i--) {
    const [start, end] = finalMerged[i]
    const before = result.slice(0, start)
    const highlight = result.slice(start, end + 1)
    const after = result.slice(end + 1)
    result = `${before}<span class="search-highlight">${highlight}</span>${after}`
  }

  return result
}

function handleExpand() {
  showModal.value = true
  buryPointFunc('全局搜索-打开搜索')
}

function handleSearch() {
  if (!searchQuery.value.trim()) {
    searchResults.value = []
    return
  }

  if (searchTimer.value) {
    clearTimeout(searchTimer.value)
  }

  searchTimer.value = setTimeout(() => {
    if (!searchRegistry.value) {
      console.warn('[g-search] SearchRegistry 未初始化')
      return
    }

    // 使用 SearchRegistry 的 Fuse.js 模糊搜索
    const results = searchRegistry.value.searchByKeyword(
      searchQuery.value.trim()
    )
    searchResults.value = results
  }, 200)
}

function handleClearQuery() {
  searchQuery.value = ''
  searchResults.value = []
  if (searchInputRef.value) {
    searchInputRef.value.focus()
  }
}

function handleClose() {
  showModal.value = false
  // searchQuery.value = ''
  // searchResults.value = []
  // activeIndex.value = 0
}

async function handleSelectItem(item: ISearchAnchor, fromHistory = false) {
  buryPointFunc('全局搜索-确认搜索')
  if (!searchRegistry.value) {
    console.warn('SearchRegistry 未初始化')
    return
  }
  // 添加到搜索历史（如果不是从历史记录点击的）
  if (!fromHistory) {
    // 从 item 中移除 _matches 属性后再保存
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { _matches, ...cleanItem } = item as any
    addToHistory(cleanItem)
  } else {
    // 如果是从历史记录点击，也更新位置（移到最前面）
    addToHistory(item)
  }

  // 更新生产线数据（如果需要）
  if (selectedLineId.value) {
    handleChangeProductionLine(selectedLineId.value)
  }
  // 为当前分类的动态ID添加数据
  if (item.category) {
    addDynamicIdDataToCategory(item.category)
  }

  // 使用 SearchRegistry 进行导航
  try {
    handleClose()
    await searchRegistry.value.navigateToAnchor(item, router, {
      waitTimeout: 50,
    })
  } catch (error) {
    console.error('[g-search] 导航失败:', error)
  }
}

function handleChangeProductionLine(id: number) {
  const obj = productionLines.value.find((item) => item.value === id)
  searchRegistry.value?.registerAppQueryData({
    setting_id: obj?.value,
    line: obj?.label,
  })
}

/** @description 为当前分类的动态ID添加数据 */
function addDynamicIdDataToCategory(category: string) {
  if (category === 'equipment') {
    const index = productionLines.value.findIndex(
      (item) => item.value === selectedLineId.value
    )
    if (index !== -1) {
      category = `/${category}`
      searchRegistry.value?.registerDynamicParams(category, {
        line_id: productionLines.value[index].value,
        line_index: index,
      })
    }
  }
}
function scrollToCategory(el: HTMLElement | undefined) {
  if (el) {
    const target = Array.isArray(el) ? el[0] : el
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }
}

/**
 * @description 滚动到激活的 item
 */
async function scrollToActiveItem() {
  await nextTick()
  const searchBody = document.querySelector('.search-body')
  const activeItem = searchBody?.querySelector('.result-item.is-active')
  if (activeItem) {
    activeItem.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest',
    })
  }
}

/**
 * @description 处理鼠标移动，退出键盘导航模式
 */
function handleMouseMove() {
  if (isKeyboardNavigating.value) {
    isKeyboardNavigating.value = false
  }
}

/**
 * @description 处理鼠标进入 item，更新激活索引
 */
function handleMouseEnterItem(index: number) {
  if (!isKeyboardNavigating.value) {
    activeIndex.value = index
  }
}

function setGroupRef(el: any, className: string) {
  if (el) {
    const element = (el as any).$el || el
    if (element instanceof HTMLElement) {
      groupRefs.value[`group-${className}`] = element
    } else if (Array.isArray(element)) {
      groupRefs.value[`group-${className}`] = element
    }
  }
}

function handleKeydown(e: KeyboardEvent) {
  const totalItems = flatResultItems.value.length
  switch (e.key) {
    case 'Escape':
      handleClose()
      break
    case 'ArrowDown':
      e.preventDefault()
      if (totalItems > 0) {
        isKeyboardNavigating.value = true
        activeIndex.value = (activeIndex.value + 1) % totalItems
        scrollToActiveItem()
      }
      break
    case 'ArrowUp':
      e.preventDefault()
      if (totalItems > 0) {
        isKeyboardNavigating.value = true
        activeIndex.value = (activeIndex.value - 1 + totalItems) % totalItems
        scrollToActiveItem()
      }
      break
    case 'Enter':
      if (totalItems > 0 && flatResultItems.value[activeIndex.value]) {
        const isFromHistory =
          !searchQuery.value && searchHistory.value.length > 0
        handleSelectItem(
          flatResultItems.value[activeIndex.value],
          isFromHistory
        )
      }
      break
  }
}

/** 获取后台配置的分类名称映射 */
function showCategoryText(className: string) {
  return (
    categoryList.find((item) => item.value === className)?.label || className
  )
}

/**
 * @description 向外暴露打开搜索弹窗方法
 */
function open() {
  handleExpand()
}

/**
 * @description 向外暴露关闭搜索弹窗方法
 */
function close() {
  handleClose()
}

expose({
  open,
  close,
})

return { __sfc: true,MAX_HISTORY_COUNT, SEARCH_HISTORY_STORAGE_KEY, searchAnchors, showModal, searchQuery, searchResults, searchHistory, searchTimer, activeIndex, isKeyboardNavigating, selectedLineId, searchInputRef, groupRefs, searchRegistry, isLoadDone, isLoading, router, productionLines, emits, groupedResults, flatResultItems, lastFKeyPressTime, DOUBLE_CLICK_DELAY, hasFocusedEditableElement, init, getSearchAnchorList, getProductionLineSettingList, extractCategory, loadHistoryFromStorage, saveHistoryToStorage, syncHistoryWithLatestData, addToHistory, handleClearHistory, highlightText, handleExpand, handleSearch, handleClearQuery, handleClose, handleSelectItem, handleChangeProductionLine, addDynamicIdDataToCategory, scrollToCategory, scrollToActiveItem, handleMouseMove, handleMouseEnterItem, setGroupRef, handleKeydown, showCategoryText, open, close }
}

})